import React, { useState, useEffect } from 'react';
import { Plus, X, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../../components/DataTable/DataTable';
import Breadcrumb from '../../../components/Common/Breadcrumb';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import salesPipelineService from '../../../services/salesPipelineService';
import { toast } from 'react-toastify';
import '../../../components/ConfirmationModal/ConfirmationModal.css';

const SalesStageMaster = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    order: 0,
    defaultProbability: 0,
    isClosedStage: false,
    isActive: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await salesPipelineService.getStages();
      if (res?.success) {
        setData((res.data || []).map(item => ({ ...item, id: item._id })));
      } else {
        toast.error('Failed to load stages');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load stages');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', order: 0, defaultProbability: 0, isClosedStage: false, isActive: true });
    setEditingItem(null);
  };

  const handleAdd = () => {
    resetForm();
    setFormData(f => ({ ...f, order: data.length }));
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      order: item.order ?? 0,
      defaultProbability: item.defaultProbability ?? 0,
      isClosedStage: !!item.isClosedStage,
      isActive: item.isActive !== false
    });
    setShowModal(true);
  };

  const handleDelete = (item) => {
    setDeleteItem(item);
    setShowDeleteModal(true);
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      toast.error('Stage name is required');
      return;
    }
    try {
      if (editingItem) {
        const res = await salesPipelineService.updateStage(editingItem._id, formData);
        if (res?.success) {
          toast.success('Stage updated');
          setShowModal(false);
          loadData();
        } else {
          toast.error(res?.message || 'Failed to update');
        }
      } else {
        const res = await salesPipelineService.createStage(formData);
        if (res?.success) {
          toast.success('Stage created');
          setShowModal(false);
          loadData();
        } else {
          toast.error(res?.message || 'Failed to create');
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save');
    }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    try {
      const res = await salesPipelineService.deleteStage(deleteItem._id);
      if (res?.success) {
        toast.success('Stage deleted');
        setShowDeleteModal(false);
        setDeleteItem(null);
        loadData();
      } else {
        toast.error(res?.message || 'Failed to delete');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete');
    }
  };

  const columns = [
    { accessorKey: 'order', header: 'Order', cell: ({ getValue }) => getValue() ?? 0 },
    { accessorKey: 'name', header: 'Name', cell: ({ getValue }) => <span className="fw-medium">{getValue()}</span> },
    {
      accessorKey: 'defaultProbability',
      header: 'Default Probability (%)',
      cell: ({ getValue }) => <span>{getValue() ?? 0}%</span>
    },
    {
      accessorKey: 'isClosedStage',
      header: 'Closed Stage',
      cell: ({ getValue }) => <span className={`badge ${getValue() ? 'bg-secondary' : 'bg-info'}`}>{getValue() ? 'Yes' : 'No'}</span>
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ getValue }) => <span className={`badge ${getValue() ? 'bg-success' : 'bg-secondary'}`}>{getValue() ? 'Active' : 'Inactive'}</span>
    }
  ];

  const breadcrumbItems = [
    { label: 'Sales', path: '/sales', onClick: () => navigate('/sales') },
    { label: 'Masters', path: '/sales?tab=masters', onClick: () => navigate('/sales?tab=masters') },
    { label: 'Sales Stage Master', path: '/sales/stage-master', isCurrent: true }
  ];

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section"><Breadcrumb items={breadcrumbItems} /></div>
      <div className="page-header">
        <div className="page-title">
          <h1>Sales Stage Master</h1>
          <p>Manage pipeline stages and default win probability</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleAdd}><Plus size={16} /> Add Stage</button>
        </div>
      </div>
      <div className="data-table-section">
        <DataTable data={data} columns={columns} loading={loading} onEdit={handleEdit} onDelete={handleDelete} noCard pageSize={10} />
      </div>

      {showModal && (
        <div className="confirmation-modal-overlay" onClick={(e) => e.target === e.currentTarget && (setShowModal(false), resetForm())}>
          <div className="confirmation-modal" style={{ maxWidth: '420px' }}>
            <div className="confirmation-modal-header">
              <h3 className="confirmation-modal-title mb-0">{editingItem ? 'Edit Stage' : 'Add Stage'}</h3>
              <button className="confirmation-modal-close" onClick={() => (setShowModal(false), resetForm())} aria-label="Close"><X size={20} /></button>
            </div>
            <div className="confirmation-modal-content" style={{ padding: '1.5rem' }}>
              <div className="mb-3">
                <label className="form-label">Name *</label>
                <input type="text" className="form-control" value={formData.name} onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Qualified" />
              </div>
              <div className="mb-3">
                <label className="form-label">Order</label>
                <input type="number" min="0" className="form-control" value={formData.order} onChange={(e) => setFormData(f => ({ ...f, order: parseInt(e.target.value, 10) || 0 }))} />
              </div>
              <div className="mb-3">
                <label className="form-label">Default Probability (0–100) %</label>
                <input type="number" min="0" max="100" className="form-control" value={formData.defaultProbability} onChange={(e) => setFormData(f => ({ ...f, defaultProbability: Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0)) }))} />
              </div>
              <div className="form-check mb-3">
                <input type="checkbox" className="form-check-input" id="closed" checked={formData.isClosedStage} onChange={(e) => setFormData(f => ({ ...f, isClosedStage: e.target.checked }))} />
                <label className="form-check-label" htmlFor="closed">Closed stage (Won/Lost)</label>
              </div>
              <div className="form-check">
                <input type="checkbox" className="form-check-input" id="act" checked={formData.isActive} onChange={(e) => setFormData(f => ({ ...f, isActive: e.target.checked }))} />
                <label className="form-check-label" htmlFor="act">Active</label>
              </div>
            </div>
            <div className="confirmation-modal-footer" style={{ padding: '1rem 1.5rem' }}>
              <button className="confirmation-modal-cancel" onClick={() => (setShowModal(false), resetForm())}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}>{editingItem ? 'Update' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal isOpen={showDeleteModal} onClose={() => (setShowDeleteModal(false), setDeleteItem(null))} onConfirm={confirmDelete}
        title="Delete Stage" message={`Delete "${deleteItem?.name}"?`} confirmText="Delete" type="danger" />
    </div>
  );
};

export default SalesStageMaster;
